/**
 * Login Page
 * Authentication page with corporate Patagonia Vital design
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Eye, EyeOff, Loader } from 'lucide-react';
import { signIn, isAuthenticated } from '../services/authService';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        navigate('/dashboard');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate inputs
    if (!email || !password) {
      setError('Por favor ingrese email y contraseña');
      setLoading(false);
      return;
    }

    // Attempt sign in
    const { user, error: signInError } = await signIn(email, password);

    if (signInError) {
      // Handle specific error messages
      if (signInError.message.includes('Invalid login credentials')) {
        setError('Email o contraseña incorrectos');
      } else if (signInError.message.includes('Email not confirmed')) {
        setError('Por favor confirme su email antes de iniciar sesión');
      } else {
        setError('Error al iniciar sesión. Por favor intente nuevamente.');
      }
      setLoading(false);
      return;
    }

    if (user) {
      // Successful login - navigate to dashboard
      navigate('/dashboard');
    } else {
      setError('Error al iniciar sesión. Por favor intente nuevamente.');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Background Image */}
      <div className="login-background">
        <img src="/images/paisaje-1.webp" alt="Patagonia Vital" />
        <div className="login-background-overlay"></div>
      </div>

      {/* Login Card */}
      <div className="login-card">
        {/* Logo */}
        <div className="login-logo">
          <img src="/images/Logo.png" alt="Patagonia Vital Logo" />
        </div>

        {/* Title */}
        <h1 className="login-title">Bienvenido</h1>
        <p className="login-subtitle">Sistema de Gestión Patagonia Vital</p>

        {/* Error Message */}
        {error && (
          <div className="login-error">
            <span>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {/* Email Input */}
          <div className="login-input-group">
            <label htmlFor="email" className="login-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@patagoniavital.com"
              className="login-input"
              disabled={loading}
              autoComplete="email"
              autoFocus
            />
          </div>

          {/* Password Input */}
          <div className="login-input-group">
            <label htmlFor="password" className="login-label">
              Contraseña
            </label>
            <div className="login-password-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="login-input"
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="login-password-toggle"
                disabled={loading}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="login-forgot-password">
            <Link to="/forgot-password" className="login-forgot-password-link">
              ¿Olvidó su contraseña?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="login-button-spinner" size={20} />
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <LogIn size={20} />
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="login-footer">
          <p>© {new Date().getFullYear()} Patagonia Vital. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
